(() => {
  'use strict';

  const STORAGE_KEY = 'manuzen:menu-drawer-state:v1';
  const MAX_STATE_AGE = 12 * 60 * 60 * 1000;
  const DRAWER_SELECTOR = 'header-drawer, menu-drawer';
  const CHILD_DETAILS_SELECTOR = 'details[id]:not(#Details-menu-drawer-container)';

  const shortcuts = [
    { href: 'https://mon-compte.manuzen.fr', label: 'Mon compte', icon: '👤' },
    { href: 'https://mon-compte.manuzen.fr/orders', label: 'Mes commandes', icon: '📦' },
    { href: '/apps/17TRACK', label: 'Suivre ma commande', icon: '⌖' },
    { href: '/pages/contactez-nous', label: 'Nous contacter', icon: '✉' },
  ];

  function readState() {
    try {
      const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (!value || !Array.isArray(value.openIds)) return null;
      if (value.savedAt && Date.now() - value.savedAt > MAX_STATE_AGE) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return value;
    } catch (_error) {
      return null;
    }
  }

  function writeState(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, savedAt: Date.now() }));
    } catch (_error) {
      // Navigation remains fully usable when storage is blocked.
    }
  }

  function mainDetails(drawer) {
    return drawer.querySelector('#Details-menu-drawer-container') || drawer.querySelector('details');
  }

  function scrollContainer(drawer) {
    const candidates = [
      drawer.querySelector('.menu-drawer__inner-container'),
      drawer.querySelector('.menu-drawer__navigation-container'),
      drawer.querySelector('.menu-drawer__navigation'),
    ].filter(Boolean);

    return candidates.find((element) => element.scrollHeight > element.clientHeight + 1) || candidates[0] || null;
  }

  function openChildIds(drawer) {
    return Array.from(drawer.querySelectorAll(CHILD_DETAILS_SELECTOR))
      .filter((details) => details.open)
      .map((details) => details.id)
      .filter(Boolean);
  }

  function saveDrawerState(drawer, options = {}) {
    const previous = readState() || {};
    const openIds = openChildIds(drawer);
    const scrollingElement = scrollContainer(drawer);

    if (openIds.length === 0 && options.allowEmpty !== true) {
      writeState({
        ...previous,
        scrollTop: scrollingElement ? scrollingElement.scrollTop : previous.scrollTop || 0,
      });
      return;
    }

    writeState({
      openIds,
      scrollTop: scrollingElement ? scrollingElement.scrollTop : 0,
    });
  }

  function saveBackState(drawer, closingDetails) {
    if (!closingDetails) {
      saveDrawerState(drawer, { allowEmpty: true });
      return;
    }

    const excludedIds = new Set(
      [closingDetails, ...closingDetails.querySelectorAll(CHILD_DETAILS_SELECTOR)]
        .map((details) => details.id)
        .filter(Boolean)
    );
    const scrollingElement = scrollContainer(drawer);

    writeState({
      openIds: openChildIds(drawer).filter((id) => !excludedIds.has(id)),
      scrollTop: scrollingElement ? scrollingElement.scrollTop : 0,
    });
  }

  function markDetailsOpen(details) {
    details.open = true;
    details.classList.add('menu-opening');

    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');

    const parentMenu = details.closest('.has-submenu');
    if (parentMenu) parentMenu.classList.add('submenu-open');
  }

  function restoreDrawerState(drawer, { activatePanel = false } = {}) {
    const state = readState();
    if (!state) return;

    const rootDetails = mainDetails(drawer);
    const childDetails = Array.from(drawer.querySelectorAll(CHILD_DETAILS_SELECTOR));
    const detailsById = new Map(childDetails.map((details) => [details.id, details]));

    drawer.querySelectorAll('.submenu-open').forEach((element) => {
      element.classList.remove('submenu-open');
    });

    childDetails.forEach((details) => {
      details.open = false;
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });

    let currentDetails = null;

    for (let index = state.openIds.length - 1; index >= 0; index -= 1) {
      currentDetails = detailsById.get(state.openIds[index]) || null;
      if (currentDetails) break;
    }

    const restoredPath = [];

    while (currentDetails && currentDetails !== rootDetails) {
      restoredPath.unshift(currentDetails);
      currentDetails = currentDetails.parentElement?.closest('details[id]') || null;
    }

    restoredPath.forEach(markDetailsOpen);

    const activeDetails = restoredPath[restoredPath.length - 1] || null;

    if (activatePanel && activeDetails) {
      const activePanel = activeDetails.querySelector(':scope > .menu-drawer__submenu');
      const backButton = activePanel?.querySelector(
        ':scope > .menu-drawer__inner-submenu > .menu-drawer__close-button'
      );

      if (activePanel && backButton) {
        if (typeof trapFocus === 'function') {
          trapFocus(activePanel, backButton);
        } else {
          backButton.focus({ preventScroll: true });
        }
      }
    }

    const scrollingElement = scrollContainer(drawer);
    if (scrollingElement && Number.isFinite(Number(state.scrollTop))) {
      requestAnimationFrame(() => {
        scrollingElement.scrollTop = Number(state.scrollTop);
      });
    }
  }

  function scheduleVisibleRestore(drawer) {
    const rootDetails = mainDetails(drawer);
    if (!rootDetails?.open) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (rootDetails.open) restoreDrawerState(drawer, { activatePanel: true });
      });
    });
  }

  function addShortcuts(drawer) {
    if (drawer.querySelector('[data-mz-menu-shortcuts]')) return;

    const navigation = drawer.querySelector('.menu-drawer__navigation');
    if (!navigation || !navigation.parentNode) return;

    const shortcutNav = document.createElement('nav');
    shortcutNav.className = 'mz-menu-shortcuts';
    shortcutNav.dataset.mzMenuShortcuts = '';
    shortcutNav.setAttribute('aria-label', 'Accès rapides');

    shortcuts.forEach((shortcut) => {
      const link = document.createElement('a');
      link.className = 'mz-menu-shortcut';
      link.href = shortcut.href;

      const icon = document.createElement('span');
      icon.className = 'mz-menu-shortcut__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = shortcut.icon;

      const label = document.createElement('span');
      label.className = 'mz-menu-shortcut__label';
      label.textContent = shortcut.label;

      link.append(icon, label);
      shortcutNav.appendChild(link);
    });

    navigation.parentNode.insertBefore(shortcutNav, navigation);
  }

  function bindDrawer(drawer) {
    if (!drawer || drawer.dataset.mzNavigationBound === 'true') return;
    drawer.dataset.mzNavigationBound = 'true';

    addShortcuts(drawer);

    const rootDetails = mainDetails(drawer);
    const scrollingElement = scrollContainer(drawer);
    let scrollFrame = 0;

    rootDetails?.addEventListener('toggle', (event) => {
      if (event.target === rootDetails && rootDetails.open) scheduleVisibleRestore(drawer);
    });

    if (rootDetails?.open) scheduleVisibleRestore(drawer);

    drawer.addEventListener('click', (event) => {
      const backButton = event.target.closest('.menu-drawer__close-button');
      const summary = event.target.closest('summary');
      const link = event.target.closest('a[href]');

      if (backButton) {
        // Save the user's intent immediately. Dawn keeps the closing details
        // element open during its animation, so reading the DOM later is racy.
        saveBackState(drawer, backButton.closest('details'));
        return;
      }

      if (summary) {
        if (summary.closest('details') !== rootDetails) {
          window.setTimeout(() => saveDrawerState(drawer, { allowEmpty: true }), 180);
        }
        return;
      }

      if (link && drawer.contains(link)) saveDrawerState(drawer, { allowEmpty: true });
    });

    drawer.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return;
      window.setTimeout(() => {
        if (rootDetails && rootDetails.open) saveDrawerState(drawer, { allowEmpty: true });
      }, 500);
    });

    if (scrollingElement) {
      scrollingElement.addEventListener(
        'scroll',
        () => {
          if (scrollFrame) cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            saveDrawerState(drawer);
            scrollFrame = 0;
          });
        },
        { passive: true }
      );
    }
  }

  function initialize(root = document) {
    if (root.matches && root.matches(DRAWER_SELECTOR)) bindDrawer(root);
    root.querySelectorAll?.(DRAWER_SELECTOR).forEach(bindDrawer);
  }

  function start() {
    initialize(document);
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;

      document.querySelectorAll(DRAWER_SELECTOR).forEach((drawer) => {
        if (drawer.dataset.mzNavigationBound === 'true') {
          scheduleVisibleRestore(drawer);
        } else {
          bindDrawer(drawer);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

